/** Canonical audio trim timing — milliseconds integers (VistoeraTASK-00165). */

export const TRIM_MIN_DURATION_MS = 100

export function clampMs(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)))
}

/** Display `MM:SS.s` with 100ms precision. */
export function formatAudioTimeMs(ms: number): string {
  const totalCs = Math.max(0, Math.round(ms / 100))
  const totalTenths = totalCs
  const tenths = totalTenths % 10
  const totalSec = Math.floor(totalTenths / 10)
  const sec = totalSec % 60
  const min = Math.floor(totalSec / 60) % 60
  const hour = Math.floor(totalSec / 3600)
  const pad2 = (n: number) => String(n).padStart(2, '0')
  if (hour > 0) {
    return `${hour}:${pad2(min)}:${pad2(sec)}.${tenths}`
  }
  return `${pad2(min)}:${pad2(sec)}.${tenths}`
}

export function formatDurationSecondsMs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}`
}

export function parseAudioTimeInput(raw: string): number | null {
  const text = raw.trim()
  if (!text) return null
  const m = text.match(/^(\d+):(\d{1,2})(?:\.(\d))?$/)
  if (!m) return null
  const min = Number(m[1])
  const sec = Number(m[2])
  const tenths = m[3] != null ? Number(m[3]) : 0
  if (!Number.isFinite(min) || !Number.isFinite(sec) || !Number.isFinite(tenths)) return null
  if (sec >= 60) return null
  return (min * 60 + sec) * 1000 + tenths * 100
}

export function parseDurationSecondsInput(raw: string): number | null {
  const text = raw.trim().replace(/秒$/, '').trim()
  if (!text) return null
  const value = Number(text)
  if (!Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 1000)
}

export type TrimSelectionMs = {
  startMs: number
  endMs: number
}

export function selectionDurationMs(sel: TrimSelectionMs): number {
  return sel.endMs - sel.startMs
}

/** Default: full source (task DEFAULT_SELECTION_POLICY). */
export function defaultTrimSelection(sourceDurationMs: number): TrimSelectionMs {
  const end = Math.max(TRIM_MIN_DURATION_MS, sourceDurationMs)
  return { startMs: 0, endMs: end }
}

export function clampSelectionToSource(sel: TrimSelectionMs, sourceDurationMs: number): TrimSelectionMs {
  const maxEnd = sourceDurationMs
  let startMs = clampMs(sel.startMs, 0, maxEnd - TRIM_MIN_DURATION_MS)
  let endMs = clampMs(sel.endMs, startMs + TRIM_MIN_DURATION_MS, maxEnd)
  if (endMs - startMs < TRIM_MIN_DURATION_MS) {
    endMs = Math.min(maxEnd, startMs + TRIM_MIN_DURATION_MS)
  }
  return { startMs, endMs }
}

export function moveSelectionKeepingDuration(
  startMs: number,
  durationMs: number,
  sourceDurationMs: number,
): TrimSelectionMs {
  const dur = Math.max(TRIM_MIN_DURATION_MS, durationMs)
  const maxStart = Math.max(0, sourceDurationMs - dur)
  const nextStart = clampMs(startMs, 0, maxStart)
  return { startMs: nextStart, endMs: nextStart + dur }
}

export function adjustStartKeepingEnd(startMs: number, endMs: number, sourceDurationMs: number): TrimSelectionMs {
  const fixedEnd = clampMs(endMs, TRIM_MIN_DURATION_MS, sourceDurationMs)
  const nextStart = clampMs(startMs, 0, fixedEnd - TRIM_MIN_DURATION_MS)
  return clampSelectionToSource({ startMs: nextStart, endMs: fixedEnd }, sourceDurationMs)
}

export function adjustEndKeepingStart(startMs: number, endMs: number, sourceDurationMs: number): TrimSelectionMs {
  const fixedStart = clampMs(startMs, 0, sourceDurationMs - TRIM_MIN_DURATION_MS)
  const nextEnd = clampMs(endMs, fixedStart + TRIM_MIN_DURATION_MS, sourceDurationMs)
  return { startMs: fixedStart, endMs: nextEnd }
}

export function adjustDurationKeepingStart(startMs: number, durationMs: number, sourceDurationMs: number): TrimSelectionMs {
  const fixedStart = clampMs(startMs, 0, sourceDurationMs - TRIM_MIN_DURATION_MS)
  const dur = Math.max(TRIM_MIN_DURATION_MS, durationMs)
  const nextEnd = clampMs(fixedStart + dur, fixedStart + TRIM_MIN_DURATION_MS, sourceDurationMs)
  return { startMs: fixedStart, endMs: nextEnd }
}

export function adjustEndFromStart(startMs: number, endMs: number, sourceDurationMs: number): TrimSelectionMs {
  return adjustEndKeepingStart(startMs, endMs, sourceDurationMs)
}
